import { Contact } from "./Contact";
import { AvisDeVirementLigne } from "./AvisDeVirementLigne";
import { DateUtils } from "../utils/DateUtils";
import _ from "lodash";

export class AvisDeVirement {
    // Miroir de AvisDeVirement::STATUT_RAPPROCHEMENT (back).
    static STATUT = {
        aucun: 0,
        partiel: 1,
        effectue: 2,
    };

    // Miroir de AvisDeVirement::MODE_RAPPROCHEMENT (back).
    static MODE = {
        auto: 0,
        manuel: 1,
        exclu: 2,
    };

    id?: number;
    client_id?: number;
    _client?: Contact;
    client_name?: string;
    date?: string;
    montant?: number;
    summary?: string;
    lignes: Array<AvisDeVirementLigne> = [];
    lignes_count?: number;
    files: Array<any> = [];
    // Rapprochement bancaire
    statut_rapprochement?: number;
    rapprochement_mode?: number;
    date_rapprochement?: string;
    groupes_total?: number;
    groupes_rapproches?: number;
    operation_bancaire_id?: number;
    operation_bancaire?: any;
    created_at?: string;
    updated_at?: string;

    constructor(data: any) {
        _.assign(this, _.omit(data, ["client", "files", "lignes"]));
        if (data.client) {
            this._client = new Contact(data.client);
        }
        if (data.files) {
            this.files = data.files;
        }
        if (data.lignes) {
            this.lignes = _.map(
                data.lignes,
                (ligne) => new AvisDeVirementLigne(ligne)
            );
        }
    }

    // Client getter/setter
    get client(): Contact | undefined {
        return this._client;
    }

    set client(value: Contact | undefined) {
        if (value) {
            this._client = value;
            this.client_id = value.id;
        }
    }

    // Date formatting getters
    get date_str(): string {
        if (this.date) {
            return DateUtils.sqlToDateStr(this.date, "DD/MM/YYYY");
        } else {
            return "";
        }
    }

    get creation_date_str(): string {
        if (this.created_at) {
            return DateUtils.sqlToDateStr(this.created_at, "DD/MM/YYYY");
        } else {
            return "";
        }
    }

    // Somme des lignes, comparée au montant de l'avis pour signaler une
    // extraction incomplète.
    get lignes_total(): number {
        return _.round(_.sumBy(this.lignes, (ligne) => ligne.montant || 0), 2);
    }

    // Rapprochement bancaire
    get is_effectue(): boolean {
        return this.statut_rapprochement === AvisDeVirement.STATUT["effectue"];
    }

    get is_partiel(): boolean {
        return this.statut_rapprochement === AvisDeVirement.STATUT["partiel"];
    }

    get is_manuel(): boolean {
        return this.rapprochement_mode === AvisDeVirement.MODE["manuel"];
    }

    get is_exclu(): boolean {
        return this.rapprochement_mode === AvisDeVirement.MODE["exclu"];
    }

    get date_rapprochement_str(): string {
        if (this.date_rapprochement) {
            return DateUtils.sqlToDateStr(this.date_rapprochement, "DD/MM/YYYY");
        } else {
            return "";
        }
    }

    // Libellé de la pastille de statut affichée dans la liste.
    get rapprochement_label(): string {
        if (this.is_manuel) {
            return "Effectué (manuel)";
        }
        if (this.is_effectue) {
            return this.date_rapprochement_str
                ? "Effectué le " + this.date_rapprochement_str
                : "Effectué";
        }
        if (this.is_partiel) {
            return this.groupes_rapproches + "/" + this.groupes_total;
        }
        if (this.is_exclu) {
            return "Exclu";
        }
        return "";
    }

    // Infobulle : opération bancaire retenue, ou cause probable d'un partiel.
    get rapprochement_title(): string {
        if (this.operation_bancaire) {
            return (
                DateUtils.sqlToDateStr(
                    this.operation_bancaire.date,
                    "DD/MM/YYYY"
                ) +
                " - " +
                this.operation_bancaire.libelle
            );
        }
        if (this.is_partiel) {
            let title =
                this.groupes_rapproches +
                " virement(s) retrouvé(s) sur " +
                this.groupes_total;
            if (
                this.lignes.length > 0 &&
                Math.abs(this.lignes_total - (this.montant || 0)) > 0.01
            ) {
                title +=
                    " - le total des lignes diffère du montant de l'avis " +
                    "(extraction possiblement incomplète)";
            }
            return title;
        }
        if (this.is_exclu) {
            return "Rapprochement automatique désactivé pour cet avis";
        }
        return "";
    }

    // Serialize method for API calls. Les lignes ne sont pas envoyées :
    // elles s'éditent unitairement via AvisDeVirementLigneApi.
    serialize(): AvisDeVirement.IAvisDeVirement {
        return {
            id: this.id,
            client_id: this.client_id,
            client_name: this.client_name,
            date: this.date,
            montant: this.montant,
            summary: this.summary,
        };
    }
}

// Interface for serialization
export namespace AvisDeVirement {
    export interface IAvisDeVirement {
        id?: number;
        client_id?: number;
        client_name?: string;
        date?: string;
        montant?: number;
        summary?: string;
    }
}
